import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Phone, Mail, Menu, X, Sparkles } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 50);
        ticking.current = false;
      });
      ticking.current = true;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-amber-100' 
        : 'bg-gradient-to-r from-stone-900/80 to-amber-950/80 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <a href="#" className="flex-shrink-0 flex items-center gap-3 group">
            <div className="relative">
              <div className={`absolute -inset-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity ${scrolled ? '' : 'opacity-50'}`} />
              <img 
                src="/images/logo.jpg" 
                alt="Shreeji Global Organisation" 
                className="relative h-14 w-14 object-contain rounded-xl shadow-lg border-2 border-amber-200/50 bg-white p-1"
              />
            </div>
            <div className={`hidden sm:block transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>
              <p className="font-bold text-lg leading-tight">Shreeji Global</p>
              <p className="text-xs text-amber-500 font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Premium Tiles & Mosaics
              </p>
            </div>
          </a>
          
          <nav className="hidden md:flex space-x-1">
            {[
              { href: '#', label: 'Home' },
              { href: '#about', label: 'About Us' },
              { href: '#products', label: 'Products' },
              { href: '#contact', label: 'Contact' },
            ].map((item) => (
              <a 
                key={item.label}
                href={item.href} 
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                  scrolled 
                    ? 'text-gray-700 hover:text-amber-700 hover:bg-amber-50' 
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className={`hidden lg:flex flex-col items-end gap-0.5 px-3 py-1 rounded-xl transition-all ${
              scrolled 
                ? 'text-gray-600' 
                : 'text-white/80'
            }`}>
              <a href="tel:+61470246506" className="flex items-center gap-2 hover:text-amber-500 transition-colors">
                <Phone className="w-3.5 h-3.5" />
                <span className="text-sm font-medium">+61 470 246 506</span>
              </a>
              <a href="mailto:shreejiglobalorganisation@gmail.com" className="flex items-center gap-2 hover:text-amber-500 transition-colors">
                <Mail className="w-3.5 h-3.5" />
                <span className="text-xs">shreejiglobalorganisation@gmail.com</span>
              </a>
            </div>

            <button 
              className={`md:hidden p-2 rounded-xl transition-all ${
                scrolled 
                  ? 'text-gray-700 hover:bg-amber-50' 
                  : 'text-white hover:bg-white/10'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className={`md:hidden py-4 border-t ${scrolled ? 'border-amber-100' : 'border-white/10'}`}>
            <nav className="flex flex-col space-y-2">
              {[
                { href: '#', label: 'Home' },
                { href: '#about', label: 'About Us' },
                { href: '#products', label: 'Products' },
                { href: '#contact', label: 'Contact' },
              ].map((item) => (
                <a 
                  key={item.label}
                  href={item.href} 
                  className={`px-4 py-3 rounded-xl font-medium transition-all ${
                    scrolled 
                      ? 'text-gray-700 hover:bg-amber-50' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <div className={`px-4 py-3 ${scrolled ? 'text-gray-600' : 'text-white/80'}`}>
                <a href="tel:+61470246506" className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4" />
                  <span className="text-sm">+61 470 246 506</span>
                </a>
                <a href="mailto:shreejiglobalorganisation@gmail.com" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span className="text-xs">shreejiglobalorganisation@gmail.com</span>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
